import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as activitiesApi from './activities.api';

import type {
  ActivityApprovalRequest,
  ActivityListQuery,
  ActivityPhoto,
  CreateActivityRequest,
  UpdateActivityRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: ActivityListQuery) => ['activities', 'list', query] as const,
  detail: (id: string) => ['activities', 'detail', id] as const,
};

export const useActivitiesList = (query: ActivityListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => activitiesApi.listActivities(query),
    staleTime: 30_000,
  });

/**
 * Facets (in-use countries + cities) for the Country / City list filters.
 * Keyed under ['activities'] so create/update/delete invalidations refresh it -
 * an activity added for a new city shows up in the filter automatically.
 */
export const useActivityFacets = () =>
  useQuery({
    queryKey: ['activities', 'facets'] as const,
    queryFn: activitiesApi.getActivityFacets,
    staleTime: 60_000,
  });

export const useActivityDetail = (id: string | null | undefined) =>
  useQuery({
    queryKey: QK.detail(id ?? ''),
    queryFn: () => activitiesApi.getActivity(id as string),
    staleTime: 30_000,
    enabled: Boolean(id),
  });

export const useCreateActivity = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateActivityRequest) => activitiesApi.createActivity(payload),
    onSuccess: (activity) => {
      // Broad key so the list AND the filter facets refresh (a new city/country
      // used by this activity should appear in the filters right away).
      qc.invalidateQueries({ queryKey: ['activities'] });
      toast.success(`Added ${activity.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add activity'),
  });
};

export const useUpdateActivity = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateActivityRequest }) =>
      activitiesApi.updateActivity(id, payload),
    onSuccess: (activity) => {
      qc.invalidateQueries({ queryKey: ['activities'] });
      qc.invalidateQueries({ queryKey: QK.detail(activity.id) });
      // After an activity's first approval a non-admin's edit is captured into
      // pendingPayload and the live record is untouched - so "Activity
      // updated" would be a lie. Say where the change actually went.
      if (activity.awaitingApproval) {
        qc.invalidateQueries({ queryKey: ['catalog-approvals'] });
        toast.success('Sent to admin for approval', {
          description:
            'Your changes are saved but stay off the live record until an admin approves.',
        });
      } else {
        toast.success('Activity updated');
      }
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update activity'),
  });
};

export const useApproveActivity = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ActivityApprovalRequest }) =>
      activitiesApi.approveActivity(id, payload),
    onSuccess: (activity, vars) => {
      qc.invalidateQueries({ queryKey: ['activities', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(activity.id) });
      toast.success(
        `${vars.payload.tier === 'admin' ? 'Admin' : 'Moderator'} ${vars.payload.approve ? 'approved' : 'approval revoked'}`,
      );
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to change approval'),
  });
};

export const useDeleteActivity = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => activitiesApi.deleteActivity(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['activities'] });
      toast.success('Activity deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete activity'),
  });
};

export const useBulkDeleteActivities = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => activitiesApi.bulkDeleteActivities(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['activities'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete activities'),
  });
};

export const useDuplicateActivity = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => activitiesApi.duplicateActivity(id),
    onSuccess: (activity) => {
      qc.invalidateQueries({ queryKey: ['activities'] });
      toast.success(`Duplicated as "${activity.name}" (draft)`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to duplicate activity'),
  });
};

/**
 * Bulk-apply a cover + gallery to many activities (Activities -> Media tool).
 * Overwrites the cover/gallery on every selected activity with the same set.
 */
export const useBulkApplyActivityMedia = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      ids: string[];
      photos: ActivityPhoto[] | null;
      galleryImageMediaIds: string[];
    }) =>
      activitiesApi.bulkApplyActivityMedia(vars.ids, {
        photos: vars.photos,
        galleryImageMediaIds: vars.galleryImageMediaIds,
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['activities'] });
      if (res.failed.length > 0) {
        toast.warning(`Applied to ${res.updated}; ${res.failed.length} failed`);
      } else {
        toast.success(`Images applied to ${res.updated} activit${res.updated === 1 ? 'y' : 'ies'}`);
      }
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to apply media'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
